import { Tags } from '@dehaat/kisan-app-bl/models/PostsV2';
import useTranslation from 'next-translate/useTranslation';
import { ChangeEvent, FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react';
import BottomDrawer from '@/components/BottomDrawer';
import LazyImage from '@/components/LazyImage';
import LoginDrawer from '@/components/LoginDrawer';
import { AuthContext } from '@/context/AuthProvider';
import { UserContext } from '@/context/UserProvider';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { ATTACHMENT_TYPE } from '@/models/Posts';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import addComment from '@/utils/api/addComment';
import uploadToS3Bucket from '@/utils/api/uploadToS3Bucket';
import { withSpecialCharFilter } from '@/utils/validation';
import CameraIcon from '../icons/CameraIcon';
import CloseIcon from '../icons/CloseIconNoBg';
import SendIcon from '../icons/SendIcon';
import SimpleUserIcon from '../icons/SimpleUserIcon';
import InPageSpinner from '../InPageSpinner';
import VoiceRecorder from './VoiceRecorder';
interface Props<T> {
  id: number;
  postType?: string;
  isFocused?: boolean;
  callback: (comment: T) => void;
  postCreator?: string;
  tags?: Tags[] | null;
}
interface SendButtonProps {
  loading: boolean;
  onClick: FormEventHandler;
}
const SendButton = ({
  loading,
  onClick
}: SendButtonProps) => {
  return loading ? <button className="flex items-center justify-center p-3 rounded-full bg-primary-100" data-sentry-component="SendButton" data-sentry-source-file="CommentsBar.tsx">
      <InPageSpinner className="border-white" data-sentry-element="InPageSpinner" data-sentry-source-file="CommentsBar.tsx" />
    </button> : <button className="flex items-center justify-center p-3 rounded-full bg-primary-100" onClick={onClick} data-testid="comments-bar-submit-button" data-sentry-component="SendButton" data-sentry-source-file="CommentsBar.tsx">
      <SendIcon className="h-6 fill-primary-100 w-6- stroke-white" data-sentry-element="SendIcon" data-sentry-source-file="CommentsBar.tsx" />
    </button>;
};
function CommentsBar<T>({
  id,
  isFocused = false,
  postType,
  postCreator,
  tags,
  callback
}: Props<T>) {
  const {
    t
  } = useTranslation('posts');
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const {
    user
  } = useContext(UserContext);
  const [comment, setComment] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [typing, setTyping] = useState(isFocused);
  const [audio, setAudio] = useState<Blob | null>(null);
  const [selectedImage, setSelectedImage] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const commentsBarRef = useRef<HTMLDivElement>(null);
  const onTextChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setComment(e.currentTarget.value);
  }, []);
  const handleInputImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      setSelectedImage(e.currentTarget.files[0]);
    }
  };
  const handleAudiochange = (newAudio: Blob) => {
    setAudio(newAudio);
  };
  const toggleDrawer = () => {
    setIsDrawerOpen(prev => !prev);
  };
  const handleUpload = async () => {
    let isFileUploaded = null;
    if (audio || selectedImage) {
      const formData = new FormData();
      formData.append('use_case', 'community');
      if (selectedImage) {
        formData.append('file', selectedImage);
        formData.append('file_type', 'image');
      } else if (audio) {
        formData.append('file', audio);
        formData.append('file_type', 'audio');
      }
      isFileUploaded = await uploadToS3Bucket(formData);
    }
    return isFileUploaded;
  };
  const handleSubmitComment: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    if (!isAuthenticated) {
      toggleDrawer();
      return;
    }
    setLoading(true);
    const isFileUploaded = await handleUpload();
    if (comment || (selectedImage || audio) && isFileUploaded) {
      let attachments = {};
      if (isFileUploaded) {
        attachments = {
          [selectedImage ? 'images' : 'audio']: [isFileUploaded]
        };
      }
      let attachmentType = ATTACHMENT_TYPE.Text;
      if (selectedImage || audio) {
        attachmentType = selectedImage ? ATTACHMENT_TYPE.Image : ATTACHMENT_TYPE.Audio;
      }
      const result = (await addComment(id, comment, postType, attachments, attachmentType)) as T | null;
      if (result) {
        const eventParams = {
          Post_Creator: postCreator,
          Post_Tags: tags?.map(tag => tag.id),
          Post_ID: id,
          Comment: comment
        };
        trackCustomEvent(CustomEventTypes.FT_POST_COMMENT_POSTED, JSON.stringify(eventParams));
        setTyping(false);
        callback({
          ...result,
          user: {
            ...user,
            photo_S3_path: user?.data?.photo_s3_path,
            is_verified: user?.profile?.is_verified
          }
        });
        if (inputRef.current) inputRef.current.value = '';
      }
    }
    //TODO:: handle the case when image/audio is not uploaded to s3
    setLoading(false);
  };
  const handleImageCancel = () => {
    if (cameraRef.current) cameraRef.current.value = '';
    setSelectedImage(null);
  };
  const handleAudioCancel = () => {
    setAudio(null);
  };

  //If user has clicked comment from the CTA buttons
  useEffect(() => {
    if (isFocused && commentsBarRef.current) {
      commentsBarRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }, [isFocused]);

  //handle change in comment bar
  useEffect(() => {
    setTyping(Boolean(comment || audio || selectedImage));
  }, [comment, audio, selectedImage]);
  return <>
      {audio && <div className="flex items-center w-full px-4 pt-2" data-testid="comments-bar-selected-audio">
          <audio controls src={URL.createObjectURL(audio)} className="w-full" />
          <CloseIcon className="w-3 h-3 ml-2 cursor-pointer" onClick={handleAudioCancel} />
        </div>}
      {selectedImage && <div className="flex justify-center w-full px-4 pt-2">
          <div className="flex">
            <LazyImage imageUrl={URL.createObjectURL(selectedImage)} alt="selected-image" fallBackIcon={<></>} className="w-20 h-20" />
            <CloseIcon className="w-3 h-3 ml-2 cursor-pointer" onClick={handleImageCancel} data-testid="selected-image-cancel-btn" />
          </div>
        </div>}
      <section className="relative flex items-center justify-between py-4" ref={commentsBarRef}>
        <div className="flex items-center justify-between w-full p-2 mr-2 text-xs border rounded-full border-neutral-30">
          {user ? <div className="relative flex justify-center w-8 h-8">
              <LazyImage imageUrl={user.data?.photo_s3_path} alt={user.full_name || ''} fallBackIcon={<div className="flex items-center justify-center p-2 rounded-full bg-neutral-10">
                    <SimpleUserIcon className="w-4 h-4 fill-neutral-40" />
                  </div>} className="w-8 h-8 rounded-full" />
            </div> : <div className="flex items-center justify-center p-2 rounded-full bg-neutral-10">
              <SimpleUserIcon className="w-4 h-4 fill-neutral-40" />
            </div>}
          <form className="flex w-full px-3" onSubmit={handleSubmitComment}>
            <input id="comment-bar" ref={inputRef} type="text" onChange={withSpecialCharFilter(onTextChange)} className="w-full outline-none" placeholder={t('total_comment_caption')} value={comment || ''} />
            {audio == null && <>
                <label htmlFor="comment-image" className="translate-y-0">
                  <CameraIcon className="w-6 h-6 fill-neutral-40" />
                </label>
                <input data-testid="comments-bar-camera-input" ref={cameraRef} type="file" accept="image/*" id="comment-image" className="hidden" onChange={handleInputImageChange} />
              </>}
          </form>
        </div>
        {typing ? <SendButton loading={loading} onClick={handleSubmitComment} /> : <VoiceRecorder callBack={handleAudiochange} />}
      </section>
      {isDrawerOpen && <BottomDrawer show={isDrawerOpen} onClose={toggleDrawer}>
          <LoginDrawer />
        </BottomDrawer>}
    </>;
}
export default CommentsBar;